import { useMemo } from 'react';

import { useSession } from '@platform/api-client/react';

/**
 * Reading the caller's permissions (M2.7, rules §17.2, §17.3, §22.2, §22.5).
 *
 * `GET /auth/session` returns the permission list so the app can hide controls a staff
 * member cannot use. **That is all it is for.** Every endpoint behind every one of these
 * controls checks the same permission on the server, and a hidden nav item is never the
 * enforcement point - rules §22.2 says so in as many words, and rule §22.5 is why
 * `can()` answers `false` while the session is still loading.
 */

export interface Permissions {
  /** Whether the caller holds a permission. `false` until the session has loaded. */
  can: (key: string) => boolean;
  /** Whether the caller holds every one of these. An empty list is always true. */
  canAll: (keys: readonly string[]) => boolean;
  /** Whether the caller holds at least one. An empty list is always true. */
  canAny: (keys: readonly string[]) => boolean;
  /** Still fetching; a guard should wait rather than decide. */
  isPending: boolean;
}

export function usePermissions(): Permissions {
  const session = useSession();
  const keys = session.data?.permissions;

  return useMemo<Permissions>(() => {
    const held = new Set<string>(keys ?? []);
    const can = (key: string): boolean => held.has(key);
    return {
      can,
      canAll: (list) => list.every(can),
      canAny: (list) => list.length === 0 || list.some(can),
      isPending: session.isPending,
    };
  }, [keys, session.isPending]);
}

/**
 * The keys the clinic admin app reads, spelled exactly as SECURITY.md §5.1 spells them.
 *
 * They are named here rather than inlined at each call site so the nav, the guards and
 * the tests cannot drift apart by a typo - a mistyped key fails *closed*, which means
 * an item silently disappears for everyone and nothing points at why.
 *
 * `clinic_staff` holds the read and day-to-day keys; `clinic_admin` additionally holds
 * the `manage` ones, which is what makes the Clinic section largely an admin section.
 */
export const ADMIN_PERMISSIONS = {
  appointmentsRead: 'appointments.read',
  patientsRead: 'patients.read',
  formsRead: 'forms.read',
  formsManage: 'forms.manage',
  formsPublish: 'forms.publish',
  providersRead: 'providers.read',
  providersManage: 'providers.manage',
  schedulesRead: 'schedules.read',
  schedulesManage: 'schedules.manage',
  appointmentTypesManage: 'appointment_types.manage',
  bookingSettingsManage: 'booking_settings.manage',
  integrationsRead: 'integrations.read',
  /* Screens for these land with their milestones; the keys are here so the nav grows a
     line rather than a lookup table when they do (see Docs/ADMIN_AUTH_UI.md §7). */
  reportsFunnel: 'reports.funnel',
  reportsFinance: 'reports.finance',
  reportsCommunications: 'reports.communications',
  reportsIntegrations: 'reports.integrations',
  logsRead: 'logs.read',
  auditRead: 'audit.read',
  usersRead: 'tenant.users.read',
  usersManage: 'tenant.users.manage',
  settingsManage: 'tenant.settings.manage',
} as const;
